<template>
  <div>
    <b-modal
      ref="database-deletion-confirmation-modal"
      :title="$t('database_credentials_deletion')"
      @ok="delete_database_credentials"
    >
      {{
        $t('confirm_deletion') +
          ' ' +
          (all_databases_credentials[deleting_index] || {}).database_name +
          '?'
      }}
      <div slot="modal-cancel">{{ $t('cancel') }}</div>
      <div slot="modal-ok">{{ $t('confirm') }}</div>
    </b-modal>
    <b-table
      class="synaplus-cosmo-scroll-higher-720"
      striped
      hover
      small
      :fields="databases_credentials_table_fields"
      :items="databases_credentials_table_items"
    >
      <template v-slot:cell(deletion)="row">
        <b-btn-group class="ml-3">
          <b-btn
            variant="outline-secondary"
            @click="toggle_database_credentials_edition(row.index)"
          >
            <span
              class="fas fa-fw"
              :class="editing_index === row.index ? 'fa-check' : 'fa-pen'"
            />
          </b-btn>

          <b-btn variant="outline-secondary" v-on="delete_button_on(row)">
            <span
              class="fas fa-fw"
              :class="editing_index === row.index ? 'fa-times' : 'fa-trash'"
            />
          </b-btn>
        </b-btn-group>
      </template>
      <template v-slot:cell(username)="row">
        <b-form-input
          v-bind="username_input_bindings(row)[0]"
          v-on="username_input_bindings(row)[1]"
        />
      </template>

      <template v-slot:cell(password)="row">
        <b-form-input
          v-bind="password_input_bindings(row)[0]"
          v-on="password_input_bindings(row)[1]"
        />
      </template>

      <template v-slot:cell(dsn)="row">
        <b-form-input
          v-bind="dsn_input_bindings(row)[0]"
          v-on="dsn_input_bindings(row)[1]"
        />
      </template>
      <template v-slot:cell(database_name)="row">
        <b-form-input
          v-bind="database_name_input_bindings(row)[0]"
          v-on="database_name_input_bindings(row)[1]"
        />
      </template>

      <template v-slot:custom-foot>
        <b-tr>
          <b-td>
            <b-form-input v-model="new_database_credentials.username" />
          </b-td>
          <b-td>
            <b-form-input v-model="new_database_credentials.password" />
          </b-td>
          <b-td>
            <b-form-input v-model="new_database_credentials.dsn" />
          </b-td>
          <b-td>
            <b-form-input v-model="new_database_credentials.database_name" />
          </b-td>
          <b-td>
            <b-btn
              :disabled="add_database_credentials_disabled"
              @click="add_new_database_credentials"
              class="ml-4"
              variant="outline-secondary"
            >
              <span class="fas fa-plus fa-fw" />
            </b-btn>
          </b-td>
        </b-tr>
      </template>
    </b-table>
  </div>
</template>

<i18n>
{
  "en": {
    "dsn": "DSN (Data Source Name)",
    "database_name": "Database name"
  },
  "fr": {
    "dsn": "DSN (Data Source Name)",
    "database_name": "Nom de la base de données"
  }
}
</i18n>

<script>
import Vue from 'vue'
import _ from 'lodash'

// *****************************************************************************

export default {
  name: 'PlatformAdminSubcomponentDatabasesCredentials',
  props: ['databases_credentials', 'new_databases_credentials'],
  data() {
    return {
      local_databases_credentials: [],
      local_new_databases_credentials: [],

      new_database_credentials: {
        username: '',
        password: '',
        dsn: '',
        database_name: '',
      },

      editing_index: -1,
      deleting_index: -1,
      editing_username_value: '',
      editing_password_value: '',
      editing_dsn_value: '',
      editing_database_name_value: '',
    }
  },

  computed: {
    databases_credentials_table_fields() {
      return [
        { key: 'username', label: this.$t('username') },
        { key: 'password', label: this.$t('password') },
        { key: 'dsn', label: this.$t('dsn') },
        { key: 'database_name', label: this.$t('database_name') },
        { key: 'deletion', label: '', class: 'w-col-2' },
      ]
    },

    databases_credentials_table_items() {
      let self = this
      const local_value = _.concat(
        this.local_databases_credentials,
        this.local_new_databases_credentials
      )
      return _.map(local_value, function(elt, index) {
        return {
          username: elt.username,
          password: elt.password,
          dsn: elt.dsn,
          database_name: elt.database_name,
        }
      })
    },

    add_database_credentials_disabled() {
      const res = !_.every(this.new_database_credentials, elt => elt !== '')
      return res
    },

    all_databases_credentials() {
      return _.concat(
        this.local_databases_credentials,
        this.local_new_databases_credentials
      )
    },
  },
  watch: {
    databases_credentials: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, this.local_databases_credentials)) {
          this.local_databases_credentials = _.cloneDeep(nv)
        }
      },
    },

    new_databases_credentials: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, this.local_new_databases_credentials)) {
          this.local_new_databases_credentials = _.cloneDeep(nv)
        }
      },
    },

    // local_value(nv, ov) {
    //   if (!_.isEqual(nv, ov)) {
    //     this.$emit('input', nv)
    //   }
    // },

    local_databases_credentials: {
      immediate: true,
      deep: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, this.databases_credentials)) {
          this.$emit('update_databases_credentials', _.cloneDeep(nv))
        }
      },
    },

    local_new_databases_credentials: {
      immediate: true,
      deep: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, this.new_databases_credentials)) {
          this.$emit('update_new_databases_credentials', _.cloneDeep(nv))
        }
      },
    },
  },

  methods: {
    username_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_index === row.index) {
        bind['value'] = this.editing_username_value
        on['input'] = event => (this.editing_username_value = event)
      } else {
        bind['value'] = row.item.username
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },
    password_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_index === row.index) {
        bind['value'] = this.editing_password_value
        on['input'] = event => (this.editing_password_value = event)
      } else {
        bind['value'] = row.item.password
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },
    dsn_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_index === row.index) {
        bind['value'] = this.editing_dsn_value
        on['input'] = event => (this.editing_dsn_value = event)
      } else {
        bind['value'] = row.item.dsn
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },
    database_name_input_bindings(row) {
      let bind = {}
      let on = {}

      if (this.editing_index === row.index) {
        bind['value'] = this.editing_database_name_value
        on['input'] = event => (this.editing_database_name_value = event)
      } else {
        bind['value'] = row.item.database_name
        bind['disabled'] = true
      }

      let res = [bind, on]
      return res
    },
    add_new_database_credentials() {
      this.local_new_databases_credentials.push(
        _.cloneDeep(this.new_database_credentials)
      )
      this.new_database_credentials = {
        username: '',
        password: '',
        dsn: '',
        database_name: '',
      }
    },

    toggle_database_credentials_edition(index) {
      if (this.editing_index === index) {
        if (index < this.local_databases_credentials.length) {
          Vue.set(
            this.local_databases_credentials[index],
            'username',
            this.editing_username_value
          )
          Vue.set(
            this.local_databases_credentials[index],
            'password',
            this.editing_password_value
          )
          Vue.set(
            this.local_databases_credentials[index],
            'dsn',
            this.editing_dsn_value
          )
          Vue.set(
            this.local_databases_credentials[index],
            'database_name',
            this.editing_database_name_value
          )
        } else {
          const nindex = index - this.local_databases_credentials.length
          Vue.set(
            this.local_new_databases_credentials[nindex],
            'username',
            this.editing_username_value
          )
          Vue.set(
            this.local_new_databases_credentials[nindex],
            'password',
            this.editing_password_value
          )
          Vue.set(
            this.local_new_databases_credentials[nindex],
            'dsn',
            this.editing_dsn_value
          )
          Vue.set(
            this.local_new_databases_credentials[nindex],
            'database_name',
            this.editing_database_name_value
          )
        }
        this.editing_username_value = ''
        this.editing_password_value = ''
        this.editing_dsn_value = ''
        this.editing_database_name_value = ''
        this.editing_index = -1
      } else {
        if (index < this.local_databases_credentials.length) {
          this.editing_username_value = this.local_databases_credentials[
            index
          ].username
          this.editing_password_value = this.local_databases_credentials[
            index
          ].password
          this.editing_dsn_value = this.local_databases_credentials[index].dsn
          this.editing_database_name_value = this.local_databases_credentials[
            index
          ].database_name
        } else {
          const nindex = index - this.local_databases_credentials.length
          this.editing_username_value = this.local_new_databases_credentials[
            nindex
          ].username
          this.editing_password_value = this.local_new_databases_credentials[
            nindex
          ].password
          this.editing_dsn_value = this.local_new_databases_credentials[
            nindex
          ].dsn
          this.editing_database_name_value = this.local_new_databases_credentials[
            nindex
          ].database_name
        }
        this.editing_index = index
      }
    },

    delete_button_on(row) {
      let res = {}

      if (this.editing_index === row.index) {
        res['click'] = () => (this.editing_index = -1)
      } else {
        res['click'] = () => this.open_database_deletion_modal(row.index)
      }

      return res
    },

    open_database_deletion_modal(index) {
      this.deleting_index = index
      this.$refs['database-deletion-confirmation-modal'].show()
    },

    delete_database_credentials() {
      this.editing_index = -1
      let index = this.deleting_index
      if (index < this.local_databases_credentials.length) {
        this.local_databases_credentials.splice(index, 1)
      } else {
        index = index - this.local_databases_credentials.length
        this.local_new_databases_credentials.splice(index, 1)
      }
      this.deleting_index = -1
    },
  },
}
</script>
